import axios from "axios";
import { ApiService, type ApiRequestConfig } from "./api.service";
import type {
  IRequestPrioritizationDraftPayload,
  IRequestPrioritizationPayload,
  IRequestPrioritizationPublishPayload,
} from "@/types/request-prioritization";

/** Machine-readable validation codes the publish endpoint returns on 400 / 409. */
type TPrioritizationErrorCode =
  | "priority_thresholds_not_monotonic"
  | "priority_points_out_of_range"
  | "priority_rule_target_mismatch"
  | "priority_config_conflict";

const PRIORITIZATION_ERROR_CODES: TPrioritizationErrorCode[] = [
  "priority_thresholds_not_monotonic",
  "priority_points_out_of_range",
  "priority_rule_target_mismatch",
  "priority_config_conflict",
];

/** Normalized validation error surfaced to the page for inline display. */
export interface IPrioritizationValidationError {
  code: TPrioritizationErrorCode | null;
  message: string;
}

/**
 * Normalize a thrown error into an {@link IPrioritizationValidationError}. The
 * publish endpoint returns `{"error": message, "code": code}` for documented
 * validation / conflict failures (400 or 409 for a version conflict); anything
 * else (network, 500) yields a `null` code so callers can fall back to a generic
 * toast. Mirrors `toSlaValidationError`. The caught value is preserved by the
 * caller's logging — this only shapes it for display.
 */
export function toPrioritizationValidationError(err: unknown): IPrioritizationValidationError {
  if (axios.isAxiosError(err) && (err.response?.status === 400 || err.response?.status === 409)) {
    const data = err.response.data as { error?: string; code?: string } | undefined;
    const code =
      data?.code && (PRIORITIZATION_ERROR_CODES as string[]).includes(data.code)
        ? (data.code as TPrioritizationErrorCode)
        : null;
    if (data?.error) return { code, message: data.error };
  }
  return { code: null, message: err instanceof Error ? err.message : String(err) };
}

/**
 * Config read + draft + publish for Request Prioritization. All endpoints hang
 * off `workspaces/<slug>/request-prioritization/`. Unpublished edits are held
 * server-side (#2867): autosave PATCHes a draft to `.../draft/` (saved, not
 * applied — no recompute), Publish PUTs the full config live, and Discard DELETEs
 * the draft. GET returns the draft overlay for editors when one exists.
 */
export class RequestPrioritizationService extends ApiService {
  /** Current config (draft overlay if any) + selectable values (auto-creates defaults on first read). */
  async getConfig(slug: string, config?: ApiRequestConfig): Promise<IRequestPrioritizationPayload> {
    return this.get(`/api/workspaces/${slug}/request-prioritization/`, config);
  }

  /** Autosave the draft (saved, not published — no recompute). Returns the
   *  config with the draft overlay + selectable values (same shape as GET). */
  async saveDraft(
    slug: string,
    payload: IRequestPrioritizationDraftPayload,
  ): Promise<IRequestPrioritizationPayload> {
    return this.patch(`/api/workspaces/${slug}/request-prioritization/draft/`, payload);
  }

  /** Discard the server-side draft; returns the published config + selectable values. */
  async discardDraft(slug: string): Promise<IRequestPrioritizationPayload> {
    return this.delete(`/api/workspaces/${slug}/request-prioritization/draft/`);
  }

  /** Publish the full config; returns the updated config + selectable values. */
  async publishConfig(
    slug: string,
    payload: IRequestPrioritizationPublishPayload,
  ): Promise<IRequestPrioritizationPayload> {
    return this.put(`/api/workspaces/${slug}/request-prioritization/`, payload);
  }

  /** Instant enable/disable of scoring (#2636) — independent of the config
   *  publish. Returns the updated config + selectable values (same shape as GET). */
  async setScoringEnabled(slug: string, enabled: boolean): Promise<IRequestPrioritizationPayload> {
    return this.patch(`/api/workspaces/${slug}/request-prioritization/scoring-enabled/`, {
      enabled,
    });
  }
}
